import { z } from 'zod'
import {
  type BoardFilters,
  STAGE_DEADLINE_PRESETS,
  type StageDeadlinePreset,
} from '@/entities/project'

// Фильтр доски целиком живёт в адресе: ссылка шарится и переживает перезагрузку.
// Наборы в URL — одним ключом через запятую (как их принимает бэк), поэтому
// прежний deep-link `?tag=vip` остаётся валидным: это набор из одного значения.

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export const BoardFilterSearchSchema = z.object({
  tag: z.string().optional().catch(undefined),
  company: z.string().optional().catch(undefined),
  responsible: z.string().optional().catch(undefined),
  priority: z.string().optional().catch(undefined),
  deadline: z.enum(STAGE_DEADLINE_PRESETS).optional().catch(undefined),
  from: z.string().regex(DATE_RE).optional().catch(undefined),
  to: z.string().regex(DATE_RE).optional().catch(undefined),
  hidden: z.boolean().optional().catch(undefined),
  archived: z.boolean().optional().catch(undefined),
})
export type BoardFilterSearch = z.infer<typeof BoardFilterSearchSchema>

/**
 * Разобранный фильтр: наборами и флагами, в таком виде им пользуется UI. Поля
 * срока присутствуют всегда (пусто — `undefined`): черновик диалога их гасит, а
 * с `exactOptionalPropertyTypes` опциональному ключу undefined не присвоить.
 */
export interface BoardFilterValue {
  companies: string[]
  responsible: string[]
  tags: string[]
  priorities: number[]
  deadline: StageDeadlinePreset | undefined
  from: string | undefined
  to: string | undefined
  hidden: boolean
  archived: boolean
}

export const EMPTY_BOARD_FILTER: BoardFilterValue = {
  companies: [],
  responsible: [],
  tags: [],
  priorities: [],
  deadline: undefined,
  from: undefined,
  to: undefined,
  hidden: false,
  archived: false,
}

function splitSet(raw: string | undefined): string[] {
  if (!raw) return []
  return [
    ...new Set(
      raw
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean),
    ),
  ]
}

function joinSet(values: readonly (string | number)[]): string | undefined {
  return values.length > 0 ? values.join(',') : undefined
}

export function boardFilterFromSearch(search: BoardFilterSearch): BoardFilterValue {
  // Диапазон и пресет взаимоисключающи: пресет в адресе перебивает границы, так
  // что собрать запрос с обоими нельзя даже правкой URL руками.
  const preset = search.deadline
  return {
    companies: splitSet(search.company),
    responsible: splitSet(search.responsible),
    tags: splitSet(search.tag),
    priorities: splitSet(search.priority)
      .map(Number)
      .filter((n) => Number.isInteger(n) && n >= 1 && n <= 10),
    deadline: preset,
    from: preset ? undefined : search.from,
    to: preset ? undefined : search.to,
    hidden: search.hidden ?? false,
    archived: search.archived ?? false,
  }
}

/** Обратно в search-параметры; пустые значения из адреса выпадают. */
export function boardFilterToSearch(value: BoardFilterValue): BoardFilterSearch {
  const range = value.deadline == null
  return {
    ...(joinSet(value.tags) ? { tag: joinSet(value.tags) } : {}),
    ...(joinSet(value.companies) ? { company: joinSet(value.companies) } : {}),
    ...(joinSet(value.responsible) ? { responsible: joinSet(value.responsible) } : {}),
    ...(joinSet(value.priorities) ? { priority: joinSet(value.priorities) } : {}),
    ...(value.deadline ? { deadline: value.deadline } : {}),
    ...(range && value.from ? { from: value.from } : {}),
    ...(range && value.to ? { to: value.to } : {}),
    ...(value.hidden ? { hidden: true } : {}),
    ...(value.archived ? { archived: true } : {}),
  }
}

/** Сколько критериев фильтра включено — число на кнопке. */
export function activeFilterCount(value: BoardFilterValue): number {
  return [
    value.companies.length > 0,
    value.responsible.length > 0,
    value.tags.length > 0,
    value.priorities.length > 0,
    value.deadline != null || value.from != null || value.to != null,
    value.hidden,
    value.archived,
  ].filter(Boolean).length
}

/** Фильтр запроса карточек воронки: пустые наборы в него не попадают. */
export function toBoardFilters(value: BoardFilterValue, boardId: string): BoardFilters {
  return {
    board_id: boardId,
    ...(value.companies.length > 0 ? { company_id: value.companies } : {}),
    ...(value.responsible.length > 0 ? { responsible_id: value.responsible } : {}),
    ...(value.tags.length > 0 ? { tag: value.tags } : {}),
    ...(value.priorities.length > 0 ? { priority: value.priorities } : {}),
    ...(value.deadline ? { stage_deadline: value.deadline } : {}),
    ...(value.deadline == null && value.from ? { stage_deadline_from: value.from } : {}),
    ...(value.deadline == null && value.to ? { stage_deadline_to: value.to } : {}),
    ...(value.hidden ? { include_hidden: true } : {}),
    ...(value.archived ? { include_archived: true } : {}),
  }
}

/** Диапазон собран задом наперёд — такой запрос бэк отвергает (400). */
export function rangeInvalid(value: Pick<BoardFilterValue, 'from' | 'to'>): boolean {
  return Boolean(value.from && value.to && value.from > value.to)
}

export const DEADLINE_PRESET_LABELS: Record<StageDeadlinePreset, string> = {
  overdue: 'Просрочен',
  today: 'Сегодня',
  '7d': '7 дней',
  '30d': '30 дней',
  none: 'Без срока',
}
